import React from "react";
import { mergeStyles, Spinner, SpinnerSize } from "@fluentui/react";
import customTheme from "helpers/customTheme";
import usePolicyEditPanelManager from "hooks/policy/usePolicyEditPanelManager";
import { PolicyEditPanelType } from "store/policyEditPanelManagementSlice";
import { ConstraintOperator } from "models/constraints";
import { useGetPolicyDetailsQuery } from "services/policy";
import { environmentConfig } from "config/environmentConfig";
import { INode } from "models/node";
import { IPolicyTemplateSlot } from "models/policySlot";
import PolicyEditStructureHeader from "./PolicyEditStructureHeader";
import PolicyEditEmptyStructure from "./PolicyEditEmptyStructure";
import PolicyEditPanelOverlay from "./PolicyEditPanelOverlay";
import { usePolicyEditStructureNodeManager } from "./PolicyEditStructureNodeManager";
import PolicyCriteriaDisplayWrapper, { IPolicyCriteria } from "./PolicyCriteriaDisplay";

const layerClass = mergeStyles({
  height: "calc(100vh - 98px)",
  overflow: "hidden",
  backgroundColor: customTheme.gridPaneBackgroundColor,
});

const contentContainerClass = mergeStyles({
  width: "65%",
  maxWidth: "1200px", // Limit content width
  backgroundColor: "white",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  borderRadius: "4px",
  overflow: "hidden",
  minHeight: "calc(100vh - 180px)",
  "& .hover-row:hover": {
    backgroundColor: customTheme.menuItemBgHovered,
    borderLeftColor: `${customTheme.focusColor} !important`
  }
});

// Main component that uses the node manager context
function PolicyEditStructure() {
  const { 
    nodeTree,
    selectNodes, clearSelection, isDeleted, policyId, policyInfo, setOnCriteriaApplied, isAddingTemplate, isDeletingTemplate } = usePolicyEditStructureNodeManager();
  const { openPanel, closePanel } = usePolicyEditPanelManager();
  
  // Close any open panel when component mounts to ensure panel is closed by default
  React.useEffect(() => {
    closePanel();
  }, [closePanel]);
  
  // Fetch policy details from API
  const { 
    isLoading: isLoadingFromApi, 
    isFetching,
    refetch 
  } = useGetPolicyDetailsQuery(
    {
      id: policyId || "",
      cloud: environmentConfig.cloud,
      environment: environmentConfig.environment,
      policyType: policyInfo?.type || "Implicit Acceptance",
    },
    {
      skip: !policyId,
      refetchOnMountOrArgChange: true,
    }
  );

  // Refetch data on mount to ensure fresh data
  React.useEffect(() => {
    if (policyId) {
      refetch();
    }
  }, [policyId, refetch]);

  // Register the refetch function so it can be called after Apply button is clicked
  React.useEffect(() => {
    if (setOnCriteriaApplied) {
      setOnCriteriaApplied(refetch);
    }
  }, [setOnCriteriaApplied, refetch]);

  // Use nodeTree.nodes as the source of truth for display
  // NodeManager populates this with proper constraint parsing
  const displayNodes = React.useMemo(() => nodeTree.nodes, [nodeTree.nodes]);

  const hasTemplates = Object.keys(displayNodes).length > 0;
  const [selectedNodeId, setSelectedNodeId] = React.useState<string | null>(null);
  const isLoading = isLoadingFromApi || isFetching || isAddingTemplate || isDeletingTemplate;

  const getSpinnerLabel = () => {
    if (isAddingTemplate) return "Loading template...";
    if (isDeletingTemplate) return "Deleting template...";
    return "Loading policy structure...";
  };

  // console.log('PolicyEditStructure render - policyId:', policyId, 'isLoading:', isLoading, 'policyData:', policyData);

  // Clear local selection if the selected node no longer exists
  React.useEffect(() => {
    if (selectedNodeId && !displayNodes[selectedNodeId]) {
      setSelectedNodeId(null);
    }
  }, [selectedNodeId, displayNodes]);

  // Convert policy slot constraints to display format
  const convertConstraintsToCriteria = (constraints: any[] = []): IPolicyCriteria[] =>
    constraints.map(constraint => ({
      id: constraint.id || constraint.keyId || Math.random().toString(),
      name: constraint.name || constraint.key || "Criterion",
      operator: constraint.operator || ConstraintOperator.Include,
      values: constraint.values || (constraint.value ? [constraint.value] : []),
      keyId: constraint.keyId,
      keyDisplay: constraint.keyDisplay,
      valueDisplay: constraint.valueDisplays ? constraint.valueDisplays.join(", ") : constraint.valueDisplay,
    }));

  const handleRowClick = (nodeId: string) => {
    if (selectedNodeId === nodeId) {
      // Deselect if clicking the same row
      setSelectedNodeId(null);
      clearSelection();
    } else {
      // Select the row
      setSelectedNodeId(nodeId);
      selectNodes([nodeId]);
    }
  };

  // Render content based on state
  const renderContent = () => {
    if (isLoading) {
      return (
        <div style={{ 
          display: "flex", 
          justifyContent: "center", 
          alignItems: "center", 
          minHeight: "200px" 
        }}>
          <Spinner size={SpinnerSize.large} label={getSpinnerLabel()} />
        </div>
      );
    }

    if (!hasTemplates || isDeleted) {
      return <PolicyEditEmptyStructure />;
    }

    return (
      <div style={{ padding: "0" }}>
        {/* Table Container with data rows only */}
        <div style={{
          border: "none",
          backgroundColor: "white"
        }}>
          {/* Table Rows */}
          {Object.values(displayNodes).map((node: INode<IPolicyTemplateSlot>) => {
            const isSelected = selectedNodeId === node.id;
            return (
              <div
                key={node.id}
                role="button"
                tabIndex={0}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1.5fr",
                  borderBottom: "1px solid #f3f2f1",
                  padding: "0",
                  backgroundColor: isSelected ? customTheme.menuItemBgPressed : "white",
                  minHeight: "36px",
                  alignItems: "flex-start",
                  cursor: "pointer",
                  borderLeft: isSelected ? `3px solid ${customTheme.focusColor}` : "3px solid transparent",
                  position: "relative",
                  transition: "all 0.1s ease"
                }}
                className={isSelected ? "" : "hover-row"}
                onClick={() => handleRowClick(node.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleRowClick(node.id);
                  }
                }}
              >
                <div style={{
                  fontWeight: 400,
                  color: "#323130",
                  fontSize: "14px",
                  padding: "12px",
                }}>
                  {node.content?.name || "Template"}
                </div>
                <div style={{ padding: "12px" }}>
                  {node.content?.constraints && node.content.constraints.length > 0 ? (
                    <PolicyCriteriaDisplayWrapper
                      criteria={convertConstraintsToCriteria(node.content.constraints)}
                    />
                  ) : (
                    <button
                      type="button"
                      style={{
                        background: "none",
                        border: "none",
                        color: "#0078d4",
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontSize: "14px",
                        padding: "0"
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (selectedNodeId !== node.id) {
                          setSelectedNodeId(node.id);
                          selectNodes([node.id]);
                        }
                        openPanel(PolicyEditPanelType.EditCriteria);
                      }}
                    >
                      Add criteria
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={layerClass}>
      <div className={contentContainerClass}>
        <PolicyEditStructureHeader />
        {renderContent()}
        <PolicyEditPanelOverlay />
      </div>
    </div>
  );
}

export default PolicyEditStructure;